import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const detailedSteps = [
  {
    step: "01",
    title: "Engage a Qualified Person (QP)",
    desc: "The Owner or Registered Tenant must engage a Qualified Person (QP) by endorsing the Authorisation Letters. The QP shall then be appointed as the Project Coordinator for the entire FSC application process.",
  },
  {
    step: "02",
    title: "Present Proposed Layout Plans",
    desc: "The Interior Designer, Main Contractor, or Owner presents the proposed layout plan(s) for the unit to the QP or Professional Engineer (PE) for review and assessment.",
  },
  {
    step: "03",
    title: "QP / PE Assessment of Site & Plans",
    desc: "The Qualified Person (QP) or Professional Engineer (PE) assesses the site and plans presented, and makes necessary comments if any. This includes checking if sprinklers are affected or if Mechanical Ventilation is affected.",
  },
  {
    step: "04",
    title: "Amend Proposed Plans if Required",
    desc: "Amendments must be made on the proposed plan(s) if required based on the QP or PE's assessment and feedback from Step 3.",
  },
  {
    step: "05",
    title: "Submit Building Fire Safety Measures Plan (BP)",
    desc: "The final, amended, or proposed Building Fire Safety Measures Plan (BP) is submitted to the Fire Safety and Shelter Department (FSSD) of SCDF for review and approval.",
  },
  {
    step: "06",
    title: "Pay Application Fee for BP",
    desc: "The application fee for the Building Plan (BP) shall be made to FSSD/SCDF upon issuance of an acknowledgement letter for the submitted plans.",
  },
  {
    step: "07",
    title: "Receive Notice of Approval (NOA)",
    desc: "SCDF/FSSD will process all the submitted documents and issue a Notice of Approval (NOA) with the necessary Reference Number once the plans are approved.",
  },
  {
    step: "08",
    title: "Submit Fire Protection (FP) & ACMV Plans",
    desc: "If Fire Protection Measures (FP) and/or Aircon-Mechanical Ventilation System (ACMV) is affected by the works, then Steps 5 through 7 shall ensue for each of these components separately, if affected or required.",
  },
  {
    step: "09",
    title: "Commence Renovation / Construction Works",
    desc: "Renovation or construction works may only commence relative to the NOA that was issued. For example, if BP NOA was issued, only BP works may commence. Sprinkler works may only commence after FP NOA is issued.",
  },
  {
    step: "10",
    title: "Follow Approved Plans Strictly",
    desc: "All A&A or renovation works must follow strictly to the submitted and approved plans. Any deviations will result in a resubmission process of the amendment plans (repeat of Steps 5 through 7). Additional costs will apply.",
  },
  {
    step: "11",
    title: "Engage Registered Inspectors",
    desc: "Upon the completion of A&A works on site, the Project Coordinator shall then arrange for the engagement of a Registered Inspector (Architect) and/or Registered Inspector (M&E) to conduct site inspections.",
  },
  {
    step: "12",
    title: "Schedule Site Inspections",
    desc: "A date shall be set for site inspections by the Registered Inspectors (Architect and/or M&E) to verify that all works comply with the approved plans and regulations.",
  },
  {
    step: "13",
    title: "Registered Inspectors Issue Compliance Documents",
    desc: "The Registered Inspector(s) shall issue Form 01/02, Appointment Letters and Annex A should they find the project site and plan to be in compliance with the necessary fire safety regulations.",
  },
  {
    step: "14",
    title: "Final Application for Fire Safety Certificate (FSC)",
    desc: "The Project Coordinator (QP/PE) shall then consolidate all documents from the entire process and make the Final Application for the Fire Safety Certificate (FSC).",
  },
];

const summarySteps = [
  { step: "1", text: "Engagement of Qualified Person (QP)" },
  { step: "2", text: "Presentation of Proposed Plans and Site Assessments" },
  { step: "3", text: "Amendments to Proposed Plan if necessary" },
  { step: "4", text: "Submission of Plan and Issuance of Acknowledgement Letter" },
  { step: "5", text: "Application Fees to FSSD/SCDF" },
  { step: "6", text: "Issuance of Notice of Approval (NOA) from SCDF/FSSD" },
  { step: "7", text: "Commencement of A&A / Renovation works on Site relative to the submitted and approved works" },
  { step: "8", text: "Submission of other components if any (Repeat Steps 4–7)" },
  { step: "9", text: "Completion of A&A / Renovation works" },
  { step: "10", text: "Engage Registered Inspector (R.I. Architect and/or R.I. M&E)" },
  { step: "11", text: "Application of FSC" },
];

export default function FscApplicationProcessPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Fire Safety Certificate (FSC) Application Process Singapore",
            description:
              "Complete step-by-step guide to the Fire Safety Certificate (FSC) application process in Singapore, including engaging a Qualified Person, submitting plans to SCDF/FSSD, obtaining Notice of Approval, and final FSC application.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/7e1be203-5a15-4e20-8904-52bf94a21333_compressed_1644aa84-afab-4054-b739-833502ed9050.webp"
              alt="Fire safety exit sign and emergency lighting installation Singapore"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">FSC Application Process</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              SCDF Compliance Guide
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Fire Safety Certificate <span className="text-primary-400">(FSC)</span> Application Process
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              A comprehensive guide to the Fire Safety Certificate application process in Singapore. Follow these steps to ensure your building project meets all SCDF and FSSD requirements for fire safety compliance.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Overview
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                What is the Fire Safety Certificate <span className="text-primary-500">(FSC)?</span>
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                The Fire Safety Certificate (FSC) is a mandatory document issued by the Singapore Civil Defence Force (SCDF) that certifies a building&apos;s fire safety measures comply with the Fire Code. Any building undergoing Additions &amp; Alterations (A&A) or renovation works that affect fire safety measures requires an FSC.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                The application process involves multiple stages — from engaging a Qualified Person (QP) to submitting building plans, obtaining Notice of Approval (NOA), and finally applying for the FSC upon completion of all works.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-6">
                The entire process is managed by a Project Coordinator (typically your appointed QP or PE) who oversees submissions, liaises with SCDF/FSSD, and coordinates inspections by Registered Inspectors.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Speak to Our QP Team
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/3331f8c9-20ce-4491-abae-aa7f42c83ebf_compressed_a71cd2b7-8133-452d-8297-50dca80fcd26.webp"
                alt="Installed emergency lighting system in Singapore building"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Important Note */}
        <section className="px-4 md:px-6 lg:px-10 pb-8 md:pb-12 max-w-7xl mx-auto">
          <div className="bg-accent-50 border border-accent-200/70 rounded-2xl p-6 md:p-8 flex items-start gap-5">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
              <i className="ri-alert-line text-accent-600 text-xl"></i>
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                Important: Pre-Approval Requirements
              </h2>
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                Approvals from URA, HDB, JTC, or BCA for Change of Use, Plan Lodgement, or any other matters related to other agencies shall be obtained first before the commencement of Fire Safety submission. Do not begin your FSC application until all relevant agency approvals are in place.
              </p>
            </div>
          </div>
        </section>

        {/* Detailed 14-Step Process */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Detailed Process
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                The <span className="text-primary-500">14-Step</span> FSC Application Process
              </h2>
              <p className="text-foreground-600 text-base max-w-2xl mx-auto">
                From engaging a Qualified Person to the final Fire Safety Certificate application, here is the complete step-by-step process managed by your Project Coordinator.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
              {detailedSteps.map((item) => (
                <div key={item.step} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-background-50 font-heading font-bold text-sm">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 text-sm md:text-base mb-2">{item.title}</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Summary Section */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Quick Reference
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                FSC Application <span className="text-primary-500">Summary</span>
              </h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto">
                A condensed overview of the entire Fire Safety Certificate application journey.
              </p>
            </div>
            <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-8">
              <ol className="flex flex-col gap-4">
                {summarySteps.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 font-semibold text-sm">{item.step}</span>
                    </div>
                    <p className="text-foreground-700 text-sm md:text-base leading-relaxed pt-1">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* Key Definitions */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Glossary
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Key <span className="text-primary-500">Terms &amp; Abbreviations</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {[
                { term: "FSC", def: "Fire Safety Certificate — the final certificate issued by SCDF upon successful compliance of all fire safety measures." },
                { term: "QP", def: "Qualified Person — a registered architect or professional engineer appointed as the Project Coordinator for the FSC application." },
                { term: "PE", def: "Professional Engineer — a registered engineer (Mechanical, Electrical, or Civil) who assesses and certifies fire safety plans." },
                { term: "SCDF", def: "Singapore Civil Defence Force — the national authority responsible for fire safety regulations and enforcement." },
                { term: "FSSD", def: "Fire Safety and Shelter Department — the SCDF department that processes fire safety plan submissions and issues approvals." },
                { term: "BP", def: "Building Fire Safety Measures Plan — the architectural plan showing fire safety provisions for the building." },
                { term: "FP", def: "Fire Protection Measures — fire safety systems including sprinklers, alarms, and smoke control." },
                { term: "ACMV", def: "Aircon-Mechanical Ventilation System — the building's air-conditioning and mechanical ventilation components." },
                { term: "NOA", def: "Notice of Approval — the official document issued by SCDF/FSSD approving submitted fire safety plans." },
                { term: "A&A", def: "Additions & Alterations — renovation or construction works that modify the existing building structure or systems." },
                { term: "R.I.", def: "Registered Inspector — a certified professional (Architect or M&E) who inspects completed works for SCDF compliance." },
                { term: "URA", def: "Urban Redevelopment Authority — agency governing land use and planning permissions in Singapore." },
                { term: "BCA", def: "Building and Construction Authority — agency regulating Singapore's building and construction industry." },
                { term: "HDB", def: "Housing & Development Board — Singapore's public housing authority." },
                { term: "JTC", def: "Jurong Town Corporation — agency managing industrial land and estates in Singapore." },
              ].map((item) => (
                <div key={item.term} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                  <span className="inline-block text-xs font-bold text-primary-600 bg-primary-50 rounded-md px-2 py-0.5 mb-2">
                    {item.term}
                  </span>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.def}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-100 border-t border-background-200/70 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Need Help with Your FSC Application?
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
              Our SCDF-registered Qualified Persons and Professional Engineers have managed hundreds of FSC applications across Singapore. From plan submissions to final inspections, we handle the entire process for you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Get FSC Application Support
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/resources/fire-safety-regulations"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                Read Fire Safety Regulations
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}